import React, { useEffect } from "react"
import { RouterProvider } from "react-router-dom"
import { computed, reaction } from "mobx"
import { observer } from "mobx-react"
import { Alert } from "antd"
import { initializeFaro } from "@grafana/faro-web-sdk"

import { authStore } from "@src/app/authStore"
import { configStore } from "@src/app/configStore"
import { GlobalCurrenciesCollection } from "@src/app/CurrenciesCollection"
import { router } from "@src/pages"
import { GlobalConvertPairsCollection } from "@src/pages/convert/classes/ConvertPairsCollection"
import { InitializingPage } from "@src/pages/initializing"
import { useDynamicHead } from "@src/shared/hooks/useDynamicHeader"
import { isSandbox } from "@src/shared/utils/isSandbox"
import s from "./index.module.css"

export const App: React.FC = observer(() => {
  const { initialize: authInitialize, isInitializing: isAuthInitializing } =
    authStore

  useDynamicHead()

  const { isLoading: isConfigLoading, load: configLoad } = configStore
  const currencies = GlobalCurrenciesCollection()
  const convertPairs = GlobalConvertPairsCollection()

  const sandbox = isSandbox()

  const initFaro = () => {
    const faroPath = process.env.REACT_APP_FARO_URL
    const faroKey = process.env.REACT_APP_FARO_API_KEY

    if (faroPath && faroKey) {
      initializeFaro({
        apiKey: faroKey,
        app: {
          environment: process.env.REACT_APP_FARO_ENV,
          name: "frontend",
          namespace: process.env.REACT_APP_FARO_PRODUCT,
          version: "1.0.0",
        },
        url: faroPath,
      })
    }
  }

  useEffect(() => {
    configLoad().then(authInitialize).then(initFaro)

    const killConvertPairsReaction = reaction(
      () => Boolean(authStore.merchant),
      (isAuthenticated) => {
        if (isAuthenticated) {
          convertPairs.load()

          return
        }

        convertPairs.reset()
      },
      { fireImmediately: true },
    )

    return killConvertPairsReaction
  }, [])

  const isInitializing = computed(
    () =>
      isConfigLoading ||
      isAuthInitializing ||
      currencies.isLoading ||
      (Boolean(authStore.merchant) &&
        (!convertPairs.isLoaded || convertPairs.isLoading)),
  ).get()

  return isInitializing ? (
    <InitializingPage />
  ) : (
    <main>
      {sandbox && (
        <Alert
          banner
          message={
            <span>
              <b>Test environment.</b>
              No market prices and no trades are binding.
            </span>
          }
          rootClassName={s.banner}
          showIcon
          type="warning"
        />
      )}
      <RouterProvider router={router} />
    </main>
  )
})
